import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useAudioNoteActiveNoteId, useAudioNoteAudioSessionId } from '@renderer/hooks/audio/use-audio-note-selectors'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useResolvedMeetingIds } from '../meeting-resolution'
import type { MeetingViewContext } from '../use-meeting-view-model'
import { meetingSessionId } from './meeting-session'

interface NoteSessionResponse {
  data?: { audioSessionId?: string }
}

/** The session the server records for a saved note. */
function useNoteSessionId(audioNoteId: string | undefined, enabled: boolean): string | undefined {
  const { getAuthToken } = useAuth()
  const { data } = useQuery({
    queryKey: ['audio-note-session', audioNoteId],
    queryFn: async () => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/audio-notes/${audioNoteId}?view=metadata`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch the meeting's session: ${response.status}`)
      }
      const json = (await response.json()) as NoteSessionResponse
      return json.data?.audioSessionId ?? null
    },
    enabled: enabled && !!audioNoteId,
    // A note's session never changes.
    staleTime: Infinity,
  })
  return data ?? undefined
}

/** The audio session behind a meeting view entry (see `meetingSessionId`). */
export function useMeetingSessionId(context: MeetingViewContext | undefined): string | undefined {
  const resolved = useResolvedMeetingIds({
    audioNoteId: context?.audioNoteId,
    calendarEventId: context?.calendarEventId,
  })
  const inputs = {
    context,
    liveSessionId: useAudioNoteAudioSessionId() ?? undefined,
    recordingNoteId: useAudioNoteActiveNoteId() ?? undefined,
    namedSessionId: resolved.audioSessionId ?? undefined,
  }
  // Asked only when nothing in this renderer knows the session.
  const noteSessionId = useNoteSessionId(resolved.audioNoteId, !meetingSessionId(inputs))
  return meetingSessionId({ ...inputs, noteSessionId })
}
